import { IntlShape } from 'react-intl';

import { PACSCommunication, PACSError, RequestSuccess } from 'app/interfaces/PACSCommunication';
import IDicomBuilder from 'app/interfaces/IDicomBuilder';
import { Study } from 'app/interfaces/Study';
import { StudyStore } from 'app/interfaces/StudyStore';
import { isAcquisitionImage } from 'app/utils/isAcquisitionImage';
import syncImageWithPACS, { PACSStoreError } from 'app/dicom/syncImageWithPACS';
import { IImageProcessor, ProcessingOptions } from 'app/interfaces/IImageProcessor';
import PROCESSING_OPTIONS from 'app/utils/PROCESSING_OPTIONS';
import { getDicomDataValue } from 'app/utils/dicom/DicomData';
import { convertCropAnnotationsToCropRect } from 'app/utils/image/convertCropAnnotationsToCropRect';
import buildProcessingOptions from 'app/utils/image/buildProcessingOptions';
import convertDisplayableImageDataToDataImage from 'app/utils/image/convertDisplayableImageDataToDataImage';
import convertDataImageToDisplayableImageData from 'app/utils/image/convertDataImageToDisplayableImageData';
import _ from 'lodash';
import adaptStoredViewportAndAnnotationsToLoadedImage from 'app/utils/image/adaptStoredViewportAndAnnotationsToLoadedImage';
import { Err, Ok, Result, isOk } from 'app/type-utils/Result';
import InvalidImageError from 'app/errors/InvalidImageError';
import { LocalDicomStore } from 'app/interfaces/LocalDicomStore';

export type SyncStudyFromStoreWithPACSDependencies = {
  intl: IntlShape;
  localDicomStore: Pick<LocalDicomStore, 'getStoredImage'>;
  studyStore: Pick<StudyStore, 'updateImage' | 'loadImage'>;
  imageProcessor: IImageProcessor;
  pacsCommunication: PACSCommunication;
  dicomBuilder: IDicomBuilder;
  isExportAllAnnotations: boolean;
  clinicInfo?: Record<string, any> | null;
};

export default async function syncStudyFromStoreWithPACS(
  study: Study,
  dependencies: SyncStudyFromStoreWithPACSDependencies
) {
  // We will iterate over each image of the study, check that they are not already synced, load the image from the store, apply processing if needed, and then sync the image with the PACS
  const {
    patient,
    images,
    _id: studyId,
    requested_procedure_id,
    study_instance_uid,
    scheduled_procedure_step_id,
    accession_number,
  } = study;

  const {
    intl,
    studyStore,
    imageProcessor,
    pacsCommunication,
    dicomBuilder,
    localDicomStore,
    isExportAllAnnotations,
  } = dependencies;
  const imagesOrder = Object.keys(images);

  const imageSavePairs = study.images.map(
    (image): [string, Promise<Result<undefined | RequestSuccess, PACSStoreError>>] => {
      const isImageNeedSync = !image.image_metadata?.PACS?.isSync && isAcquisitionImage(image);
      if (!isImageNeedSync) return [image._id, Promise.resolve(Ok(undefined))];

      let dicomData;
      const savePromise = (async () => {
        const loadedImage = await localDicomStore.getStoredImage(studyId, image._id);
        const { processedImage, dicomData: loadedDicomData, imageData } = loadedImage;
        dicomData = loadedDicomData;
        const { annotations, viewport } = adaptStoredViewportAndAnnotationsToLoadedImage({
          displayableImage: imageData,
          annotations: image.annotations,
          viewport: image.viewport,
        });

        let processedImageToSave = processedImage;
        if (!processedImageToSave) {
          if (!(imageData.getPixelData() instanceof Uint16Array)) {
            return Err(
              new InvalidImageError('Image is not a DICOM content with Uint16Array pixel data')
            );
          }
          const processingOptions: ProcessingOptions = buildProcessingOptions(image, {
            baseParameters: PROCESSING_OPTIONS[image.image_metadata?.processingType ?? 'default'],
            photometricInterpretation: getDicomDataValue(dicomData, 'PhotometricInterpretation'),
            cropRect: convertCropAnnotationsToCropRect(annotations?.Crop),
          });
          const processableImageData = convertDisplayableImageDataToDataImage(imageData);
          processedImageToSave = await imageProcessor.process(
            processableImageData,
            processingOptions
          );
        }
        dicomData = _.merge(
          dicomData ?? {},
          _.pickBy({
            RequestedProcedureID: requested_procedure_id,
            StudyInstanceUID: study_instance_uid,
            ScheduledProcedureStepID: scheduled_procedure_step_id,
            AccessionNumber: accession_number,
          })
        );

        return syncImageWithPACS(
          {
            studyId,
            image: {
              ...image,
              displayableImage: convertDataImageToDisplayableImageData(
                processedImageToSave,
                image._id
              ),
              annotations,
              viewport,
              dicomData,
            },
            patient,
            imagesOrder,
          },
          { pacsCommunication, intl, dicomBuilder, clinicInfo: dependencies.clinicInfo },
          { includeAnnotations: isExportAllAnnotations }
        ).then((result) => {
          if (isOk(result)) {
            studyStore.updateImage(studyId, image._id, {
              image_metadata: { PACS: { isSync: true } },
            });
          }
          return result;
        });
      })();

      return [image._id, savePromise];
    }
  );

  return _.fromPairs(imageSavePairs);
}
